import config from 'constants/config'
import { ITEM_STATUS_AWAITING_DATES, ITEM_STATUS_CANCELLED, ITEM_STATUS_CREATED, ITEM_STATUS_COMPLETED } from 'constants/cart'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import moment from 'moment'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getLatestCompletedReservation } from 'lib/order/orderUtils'
import { getIsMaldivesCharterBundleOrder } from 'lib/order/charterUpgradeUtils'
import { isItemUpcoming } from 'lib/order/isUpcoming'
import { ISO_DATE_FORMAT, INTL_LONG_DATE, INTL_PICKER_DATE_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { hasApprovedBNBLRefundRequest } from 'lib/order/refundRequestUtils'
import { isSelfServiceRebookAllowed } from 'lib/offer/isSelfServiceRebookAllowed'
import { canBeUpgraded } from '../HotelItemSummaryUtils'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import { orderHasInsuranceBookingProtection } from 'lib/order/orderInsuranceUtils'
import { canRefundItem } from 'lib/order/orderItemUtils'
import { PACKAGE_UPGRADE_ALLOWED_OFFER_IDS } from 'lib/offer/packageUpgradeAllowlist'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'

export interface OrderHotelChangeDatesConfig {
  canDateChange: boolean
  /**
   * exact date when daysBeforeCheckInChangesDisallowed is reached
   */
  policyDeadline: string
  policyDeadlineInstant?: string
  /**
   * Whether or not the user can request a date change through outsidePolicy DatesRequest
   * when offer's daysBeforeCheckInChangesDisallowed date is reached
   */
  canRequestDateChange: boolean
  /**
   * Formatted display string for the change dates deadline.
   * Accounts for both policyDeadline and the offer's schedule (for Always On offers).
   * Only set when canDateChange is true.
   */
  changeDatesDeadlineDisplay?: string
  /**
   * Formatted display string for the extend dates deadline.
   * Derived from min(offerScheduleEnd, effectiveChangeDatesEnd).
   * Only set when canDateChange is true and offer has an online purchase schedule.
   */
  extendDatesDeadlineDisplay?: string
}

export const canAddFlights = (order: App.Order, geo: App.GeoState, offerFlightsEnabled: boolean) => {
  if (!config.BNBL_FLIGHT_BOOKING_ENABLED) return false
  if (!offerFlightsEnabled) return false
  if (!geo.currentRegionHasFlights) return false

  const alreadyHasFlights = order.flightItems.some(
    ({ status }) => status !== ITEM_STATUS_CANCELLED,
  )
  if (alreadyHasFlights) return false

  const latestReservation = getLatestCompletedReservation(order)
  if (latestReservation && !isItemUpcoming(latestReservation)) return false

  if (order.items?.some(i => i.canBookDates)) return false

  const validStatuses = [
    ITEM_STATUS_AWAITING_DATES,
    ITEM_STATUS_CREATED,
    ITEM_STATUS_COMPLETED,
  ]
  const validAccomItems = order.items.filter(({ status }) => validStatuses.includes(status))
  return (validAccomItems.length === 1)
}

export const changeDatesUntilStr = (checkInDate: string, changeDatesLimit: number) => {
  const intl = getCurrentIntl()
  const formattedDate = intlDateFormatter(
    moment.utc(checkInDate).subtract(changeDatesLimit, 'day'),
    INTL_LONG_DATE,
    intl.locale,
    'UTC',
  )
  return intl.formatMessage({
    id: 'JjmwPq5NlqkBOUstRqdo',
    defaultMessage: '{date} ({count, plural, one {# day} other {# days}} before check-in).',
    description: 'Deadline for changing hotel booking dates, showing date and days before check-in',
  }, { date: formattedDate, count: changeDatesLimit })
}

export function canShowBookByDates(canBookDates: boolean, bookByDate: string) {
  if (!canBookDates) return false
  return moment.utc(bookByDate).subtract(2, 'month').isAfter(moment.utc('2021-01-01'))
}

export const getCapacityPath = (item: App.OrderItem, order: App.Order, originAirportCode?: string) => ({
  orderItemId: item.id,
  orderId: order.id,
  originAirportCode,
})

export const hasActiveReservation = (item: App.OrderItem) => {
  if (item.reservationError) return false
  if (!item.reservation) return false
  if (item.status === 'cancelled') return false
  return true
}

const allowedDateChangeTypes = new Set<App.OfferType>(['hotel', 'tactical_ao_hotel'])
const allowedInstallmentDateChangeTypes = new Set<App.OfferType>(['hotel'])
const allowedDepositDateChangeTypes = new Set<App.OfferType>(['hotel', 'tour'])
/**
 * @deprecated Eligibility for change dates is now backend-owned via
 * cancellationConfig.canChangeDates / canRequestDateChange.
 * Keep this temporarily for backward compatibility while remaining callers migrate.
 */
export function isDateChangeAllowed(
  item: App.OrderItem | undefined,
  numberOfDaysBeforeBookingIsLockedIn: number,
  changeDatesLimitReached: boolean,
  order: App.Order,
  cancellationConfig: App.OrderItemHotelCancellationConfig,
  offer: App.Offer,
): boolean {
  if (!item) return false
  if (item.isCompedPackage) return false
  if (!allowedDateChangeTypes.has(item.offer.type)) return false
  if (!item.reservation) return false
  if (moment().startOf('day').isAfter(moment(item.reservation.endDate))) return false
  if (item.canBookDates) return false
  if (item.status === ITEM_STATUS_CANCELLED) return false
  if (order.hasFlight) return false
  if (changeDatesLimitReached) return false
  if (order.depositDetails && !allowedDepositDateChangeTypes.has(item.offer.type)) return false
  if (order.instalmentDetails && !allowedInstallmentDateChangeTypes.has(item.offer.type)) return false
  if (item.offer.type === OFFER_TYPE_ALWAYS_ON && !cancellationConfig.canRefund) return false
  if (item.offer.type === OFFER_TYPE_ALWAYS_ON && !scheduleIsCurrent(offer.onlinePurchaseSchedule)) return false
  if (order.reserveForZeroDetails?.isActive) return false
  if (!config.accommodation.allowDateChangeOnOrder) return false
  if (!numberOfDaysBeforeBookingIsLockedIn || !item.reservation) return false

  return true
}

export function getHotelChangeDatesConfig(
  item: App.OrderItem | undefined,
  order: App.Order,
  offer: App.Offer,
  cancellationConfig: App.OrderItemHotelCancellationConfig,
) {
  const offerScheduleEnd = offer?.onlinePurchaseSchedule?.end ? moment(offer.onlinePurchaseSchedule.end).format(ISO_DATE_FORMAT) : undefined

  const configs: OrderHotelChangeDatesConfig = {
    canDateChange: false,
    policyDeadline: '',
    canRequestDateChange: false,
  }

  if (!item) return configs
  const canChangeDatesFromConfig = cancellationConfig.canChangeDates ?? false
  const canRequestDateChangeFromConfig = cancellationConfig.canRequestDateChange ?? false

  if (item.reservation) {
    // Date change is cancel-and-rebook — the earliest of the offer cutoff and
    // the cancellation-policy deadline wins.
    const offerCutoff = moment(item.reservation.startDate)
      .subtract(offer?.daysBeforeCheckInChangesDisallowed || 0, 'days')
    const cancellationPolicyDeadline = cancellationConfig.policyDeadline ? moment(cancellationConfig.policyDeadline) : null
    const cancellationDeadlineIsEffective = !!cancellationPolicyDeadline?.isBefore(offerCutoff)
    const finalDateAllowed = cancellationDeadlineIsEffective && cancellationPolicyDeadline
      ? cancellationPolicyDeadline
      : offerCutoff
    configs.policyDeadline = finalDateAllowed.format(ISO_DATE_FORMAT)
    configs.policyDeadlineInstant = cancellationDeadlineIsEffective ? cancellationConfig.policyDeadlineInstant : undefined

    configs.canDateChange = canChangeDatesFromConfig
    configs.canRequestDateChange = canRequestDateChangeFromConfig

    if (configs.canDateChange && configs.policyDeadline) {
      const intl = getCurrentIntl()
      const isAlwaysOn = item.offer.type === OFFER_TYPE_ALWAYS_ON
      const effectiveChangeDatesEnd = isAlwaysOn && offerScheduleEnd && moment(offerScheduleEnd).isBefore(configs.policyDeadline)
        ? offerScheduleEnd
        : configs.policyDeadline
      configs.changeDatesDeadlineDisplay = intlDateFormatter(effectiveChangeDatesEnd, INTL_PICKER_DATE_FORMAT, intl.locale)
      // Extend deadline can differ from change deadline for non-AO offers (capped by schedule end).
      // For AO offers, the schedule is already factored into effectiveChangeDatesEnd, so both match.
      if (offerScheduleEnd) {
        const effectiveExtendDatesEnd = moment(offerScheduleEnd).isBefore(effectiveChangeDatesEnd) ? offerScheduleEnd : effectiveChangeDatesEnd
        configs.extendDatesDeadlineDisplay = intlDateFormatter(effectiveExtendDatesEnd, INTL_PICKER_DATE_FORMAT, intl.locale)
      }
    }
  }

  return configs
}

const allowedExtendDateTypes = new Set<App.OfferType>(['hotel', 'tactical_ao_hotel'])
/**
 * @deprecated Eligibility for extend dates is now backend-owned via
 * cancellationConfig.canExtendDates.
 * Keep this temporarily for backward compatibility while remaining callers migrate.
 */
export function isExtendDatesAllowed(
  item: App.OrderItem,
  offer: App.Offer,
  order: App.Order,
  cancellationConfig?: App.OrderItemHotelCancellationConfig,
): boolean {
  if (item.status === ITEM_STATUS_CANCELLED) return false
  if (!allowedExtendDateTypes.has(offer.type)) return false
  if (!isSelfServiceRebookAllowed(offer, order, cancellationConfig)) return false
  if (offer.type === 'hotel' && item.reservation?.channelManager !== 'self-managed') return false
  return true
}

/**
 * Checks basic item state validations for upgrade eligibility.
 * This is similar to canRefundItem but doesn't check canRefund policy
 * since canUpgradePackage handles that with relaxed criteria for flash hotels.
 */
function canUpgradeItem(item: App.OrderItem, order: App.Order) {
  // can't upgrade a cancelled item
  return item.status !== 'cancelled' &&
    // can't already have a refund
    !order.refunds.some(refund => refund.itemId === item.id) &&
    // can't have a refund request against it
    !order.refundRequests[item.id] &&
    // Block upgrade if insurance claim is pending
    !order.locked
}

export function isPackageUpgradeAllowed(
  item: App.OrderItem,
  order: App.Order,
  offer: App.Offer,
  hasUnpaidDepositAmount: boolean,
  hasUnpaidInstalment: boolean,
  cancellationConfig?: App.OrderItemHotelCancellationConfig,
) {
  if (order.locked) return false

  const isComped = item.isCompedPackage
  // Payment schedule orders can now upgrade - adjustment is handled via payment schedule API
  const isReserveForZero = order.reserveForZeroDetails?.isActive
  const isMaldivesCharterOrder = getIsMaldivesCharterBundleOrder(order)
  const isGiftOrder = !!order.orderGift
  // isSelfServiceRebookAllowed uses canUpgradePackage for allowlisted offers, canAutoRefund for others
  const isRebookAllowed = isSelfServiceRebookAllowed(offer, order, cancellationConfig)
    || (isMaldivesCharterOrder && !isGiftOrder)
  const isUpgradeable = canBeUpgraded(item)
  const isFlightBundled = isFlightForceBundled(offer)
  const hasApprovedRefund = hasApprovedBNBLRefundRequest(order, item)
  const hasBookingProtection = orderHasInsuranceBookingProtection(order)

  if (PACKAGE_UPGRADE_ALLOWED_OFFER_IDS.has(offer.id)) {
    // For allowlisted offers: use canUpgradeItem (relaxed check - no canRefund policy check)
    // The canUpgradePackage flag (checked in isSelfServiceRebookAllowed) already handles
    // the relaxed upgrade eligibility criteria for flash hotels
    if (!canUpgradeItem(item, order)) return false
  } else {
    // For all other offers: use pre-existing canRefundItem + canRefund checks
    if (!canRefundItem(item, order, cancellationConfig)) return false
    if (!cancellationConfig?.canRefund) return false
  }

  return !isComped
    && !hasUnpaidDepositAmount
    && !hasUnpaidInstalment
    && !isReserveForZero
    && isRebookAllowed
    && isUpgradeable
    && !isFlightBundled
    && !hasApprovedRefund
    && !hasBookingProtection
}

// TODO: Temporary BNBL bypass for conflict in middle east - Remove when CS team gives go ahead
const LetPutOnHoldMiddleEastConflict = new Map<string, string>([
  ['006RF00000MgtwHYAR', '11/04/2026'],
  ['006RF00000HqeKvYAJ', '11/04/2026'],
  ['006RF00000OyV6LYAV', '11/04/2026'],
  ['006RF00000Gb0BCYAZ', '11/04/2026'],
  ['006RF00000KEECTYA5', '11/04/2026'],
  ['006RF00000FPHufYAH', '11/04/2026'],
  ['006RF00000JpqT3YAJ', '11/04/2026'],
  ['006RF00000Fnpn7YAB', '11/04/2026'],
  ['006RF00000MU2J4YAL', '11/04/2026'],
  ['006RF00000JBxppYAD', '11/04/2026'],
  ['006RF00000OIRVOYA5', '11/04/2026'],
  ['006RF00000OXwNZYA1', '11/04/2026'],
  ['006RF00000GgjKTYAZ', '11/04/2026'],
  ['006RF00000OxkFWYAZ', '11/04/2026'],
  ['006RF00000E5O9jYAF', '11/04/2026'],
  ['006RF00000PxnuNYAR', '11/04/2026'],
  ['006RF00000JsxMkYAJ', '11/04/2026'],
  ['006RF00000HSvO9YAL', '11/04/2026'],
  ['006RF00000O3hsFYAR', '11/04/2026'],
  ['006RF00000Ol2esYAB', '11/04/2026'],
  ['006RF00000K4csoYAB', '11/04/2026'],
  ['006RF00000KpuRFYAZ', '11/04/2026'],
  ['006Ii000002J2z9IAC', '11/04/2026'],
  ['006RF00000FPIHFYA5', '11/04/2026'],
  ['0062y00000Id2uhAAB', '11/04/2026'],
  ['0062y000007oWDLAA2', '11/04/2026'],
  ['006RF00000CnP8KYAV', '11/04/2026'],
  ['0062y00000GPgLwAAL', '11/04/2026'],
  ['006RF00000DYJ5aYAH', '11/04/2026'],
  ['006RF00000CnP8OYAV', '11/04/2026'],
  ['006RF00000HbqKPYAZ', '11/04/2026'],
  ['006Ii000002IlwXIAS', '11/04/2026'],
  ['006Ii000002HT26IAG', '11/04/2026'],
  ['006RF00000CnJdkYAF', '11/04/2026'],
])

export function canPutOnHold(
  item: App.OrderItem,
  order: App.Order,
  offerType: App.OfferType | undefined,
  canDateChange: boolean,
) {
  return (config.CONVERT_ORDER_TO_BNBL && canDateChange && !item.isCompedPackage && order.bookingProtectionItems.length === 0 && offerType === 'hotel')
}

/**
 * Check if the item should be put on hold due to Middle East conflict.
 * Each offer has a cutoff date — if check-in is between now and that date, the customer can self-serve.
 * TODO: Temporary - Remove when CS team gives go ahead
 */
export function putOnHoldMiddleEastConflict(item: App.OrderItem): boolean {
  const cutoffStr = LetPutOnHoldMiddleEastConflict.get(item.offerId)
  if (item.reservation && cutoffStr !== undefined) {
    const checkIn = moment.utc(item.reservation.startDate)
    const cutoff = moment.utc(cutoffStr, 'DD/MM/YYYY').endOf('day')
    return checkIn.isBetween(moment.utc(), cutoff, undefined, '[)')
  }
  return false
}
